import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Heart, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Theme-specific colors
const THEME_COLORS = {
  healing: {
    bg: '#E7C6C2',
    text: '#2E2536',
    accent: '#F7E5E5',
  },
  strength: {
    bg: '#6C4CA2',
    text: '#FFFFFF',
    accent: '#8B6BB8',
  },
  sisterhood: {
    bg: '#AAB49F',
    text: '#FFFFFF',
    accent: '#C8D1C0',
  },
  empowerment: {
    bg: '#7C9069',
    text: '#FFFFFF',
    accent: '#AAB49F',
  }
};

const FavoritesPage = () => {
  const navigate = useNavigate();
  const [favoritesByCategory, setFavoritesByCategory] = useState({
    healing: [],
    strength: [],
    sisterhood: [],
    empowerment: []
  });
  const [loading, setLoading] = useState(true);
  const [userSessionId] = useState(() => {
    return localStorage.getItem('user_session_id') || '';
  });

  useEffect(() => {
    if (userSessionId) {
      fetchFavorites();
    }
  }, [userSessionId]);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reels/favorites/${userSessionId}/by-category`);
      setFavoritesByCategory(response.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (reelId, category) => {
    try {
      await axios.post(`${API}/reels/favorite`, {
        user_session_id: userSessionId,
        reel_id: reelId
      });
      
      // Update local state
      setFavoritesByCategory(prev => ({
        ...prev,
        [category]: prev[category].filter(reel => reel.id !== reelId)
      }));
      
      toast.success('Removed from favorites');
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast.error('Failed to remove favorite');
    }
  };

  const totalFavorites = Object.values(favoritesByCategory).reduce((sum, arr) => sum + arr.length, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#2E2536]">
        <div className="text-center">
          <Sparkles className="w-12 h-12 text-[#C8B5E0] mx-auto mb-4 animate-pulse" />
          <p className="text-[#EDE8F3] text-lg">Loading your favorites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#2E2536]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#2E2536]/95 backdrop-blur-sm border-b border-[#C8B5E0]/20">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="icon"
              className="text-[#EDE8F3] hover:bg-[#C8B5E0]/20 rounded-full"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            
            <h1 className="text-2xl font-bold text-[#EDE8F3]">Close to My Heart</h1>
            
            <div className="w-10"></div> {/* Spacer for alignment */}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {totalFavorites === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 text-[#E7C6C2] mx-auto mb-4 opacity-50" />
            <p className="text-[#EDE8F3] text-xl mb-2">Nothing close to your heart yet</p>
            <p className="text-[#C8B5E0] mb-6">Begin your healing journey and hold onto what speaks to you</p>
            <Button
              onClick={() => navigate('/reels')}
              className="bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white px-6 py-3 rounded-full"
            >
              Paths of Healing
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Summary */}
            <div className="text-center mb-8">
              <p className="text-[#C8B5E0] text-lg">
                You have <span className="text-[#EDE8F3] font-semibold">{totalFavorites}</span> saved {totalFavorites === 1 ? 'whisper' : 'whispers'}
              </p>
            </div>

            {/* Categories */}
            {Object.entries(favoritesByCategory).map(([category, quotes]) => {
              if (quotes.length === 0) return null;
              
              const colors = THEME_COLORS[category];
              
              return (
                <div key={category} className="space-y-4">
                  {/* Category Header */}
                  <div 
                    className="flex items-center gap-3 px-4 py-2 rounded-lg"
                    style={{ backgroundColor: `${colors.bg}20` }}
                  >
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: colors.bg }}
                    />
                    <h2 
                      className="text-xl font-semibold capitalize"
                      style={{ color: colors.bg }}
                    >
                      {category}
                    </h2>
                    <span className="text-sm" style={{ color: colors.bg }}>
                      ({quotes.length})
                    </span>
                  </div>

                  {/* Quotes Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {quotes.map((reel) => (
                      <div 
                        key={reel.id}
                        className="relative rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
                        style={{ 
                          backgroundColor: colors.bg,
                          minHeight: '200px'
                        }}
                      >
                        {/* Quote Content */}
                        <div className="p-6 h-full flex flex-col justify-between">
                          <p 
                            className="text-sm leading-relaxed line-clamp-6"
                            style={{ color: colors.text }}
                          >
                            {reel.quote}
                          </p>
                          
                          {/* Actions */}
                          <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderColor: `${colors.text}30` }}>
                            <Button
                              onClick={() => navigate(`/reels/${category}`)}
                              variant="ghost"
                              size="sm"
                              className="opacity-70 hover:opacity-100"
                              style={{ color: colors.text }}
                            >
                              View Similar
                            </Button>
                            
                            <Button
                              onClick={() => removeFavorite(reel.id, category)}
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 opacity-70 hover:opacity-100"
                              style={{ color: colors.text }}
                            >
                              <Heart className="w-5 h-5 fill-current" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
